/**
 * One project's icon, resolved the same way everywhere it is drawn.
 *
 * Two provenances exist and they are not interchangeable: a CMMD-backed
 * project owns its identity (an uploaded image or an emoji), while a plain
 * local checkout is identified by the favicon discovered in its working
 * directory. A cloud project has no local cwd at all, so falling back to the
 * favicon path for one renders nothing but a letter avatar — which is why the
 * canonical branch has to come first and why every surface has to share this
 * ladder rather than re-derive it.
 *
 * This lives outside `sidebar/` on purpose: the breadcrumb, the command
 * palette, the command centre's task rows and the thread cards all draw the
 * same identity, and a sidebar-scoped component invites a second, drifting
 * copy for everything else.
 */

import type { EnvironmentId } from "@cmmd-forge/contracts";

import { ProjectFavicon } from "./ProjectFavicon";
import { ProjectIdentityIcon } from "./ProjectIdentityIcon";

/** A CMMD project's own identity, as carried by the sidebar's project groups. */
export interface ProjectCanonicalIdentity {
  readonly iconUrl: string | null;
  readonly icon: string | null;
}

export function ProjectIcon(input: {
  readonly environmentId: EnvironmentId;
  readonly cwd: string;
  readonly projectName: string | null;
  readonly canonicalIdentity?: ProjectCanonicalIdentity | undefined;
  readonly className?: string | undefined;
}) {
  if (input.canonicalIdentity !== undefined) {
    return (
      <ProjectIdentityIcon
        iconUrl={input.canonicalIdentity.iconUrl}
        icon={input.canonicalIdentity.icon}
        projectName={input.projectName ?? ""}
        className={input.className}
      />
    );
  }

  return (
    <ProjectFavicon
      environmentId={input.environmentId}
      cwd={input.cwd}
      projectName={input.projectName}
      className={input.className}
    />
  );
}
